<template>
    <div class="text-left">
        <div class="flex gap-4">
            <div class="w-1/2">
                <label :for="firstNameId" class="block text-sm font-medium text-gray-700">First name</label>
                <input
                    :id="firstNameId"
                    v-model="firstName"
                    type="text"
                    autocomplete="given-name"
                    class="mt-1 block w-full rounded-md border-gray-300 py-2 px-3 shadow-sm focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400 sm:text-sm"
                >
            </div>
            <div class="w-1/2">
                <label :for="lastNameId" class="block text-sm font-medium text-gray-700">Last name</label>
                <input
                    :id="lastNameId"
                    v-model="lastName"
                    type="text"
                    autocomplete="family-name"
                    class="mt-1 block w-full rounded-md border-gray-300 py-2 px-3 shadow-sm focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400 sm:text-sm"
                >
            </div>
        </div>

        <div class="mt-3">
            <label :for="emailId" class="block text-sm font-medium text-gray-700">Email</label>
            <input
                :id="emailId"
                v-model="email"
                type="email"
                autocomplete="email"
                class="mt-1 block w-full rounded-md border-gray-300 py-2 px-3 shadow-sm focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400 sm:text-sm"
            >
        </div>

        <div class="mt-3">
            <label class="block text-sm font-medium text-gray-700">Payment method</label>
            <div :id="paymentElementId" class="mt-1"></div>
        </div>

        <p v-if="errorMessage" class="mt-2 text-sm text-red-500" data-test="card-fields-error">{{ errorMessage }}</p>
    </div>
</template>

<script setup>
// A self-contained Stripe card-entry component (86bc9e403). Unlike AddCardPopup it owns no
// modal chrome and no subscribe logic: it just collects a card and, on demand, confirms the
// SetupIntent and persists the payment method. The combined RiskGuard Pro checkout embeds it
// inline so the landlord can enter a card and subscribe in one pop-up.
//
// Modeled on AddCardPopup's includeStripe / configureStripe / storeCard, but rebuilt as a
// standalone component so AddCardPopup (a site-wide P1 component) is never touched. The Stripe
// element DOM ids are uniquified with useId() so this can coexist with any other card form on
// the page.
//
// 86bckgczc: one Payment Element replaces the four split card fields, so Apple Pay and Google
// Pay are offered next to the card. They ride on the SetupIntent's `card` type (no backend
// change) and save as an ordinary card. Link is turned off, matching StripeForm (86bcfenhv).
import { markRaw, onMounted, ref, useId, watch } from 'vue'
import axios from 'axios'

const emit = defineEmits(['complete'])

const props = defineProps({
    // FunctionHelpers::stripeDataForAddingCard(): publishable_key, setup_intent.client_secret,
    // first_name, last_name, user.email.
    stripeData: {
        type: Object,
        required: true,
    },
    // The parent renders this behind a v-if/v-show; when it becomes visible we (re)configure
    // the Stripe elements. We configure on mount and on visibility — never on a `show` watch
    // of a modal, so the fields mount against a DOM node that actually exists.
    visible: {
        type: Boolean,
        default: true,
    },
    // 86bckgczc: Apple Pay's recurringPaymentRequest for a subscription checkout. Passing it
    // asks Apple for a merchant token (MPAN), which keeps renewals working after the landlord
    // changes devices. Null keeps the plain Apple Pay card.
    applePayRecurring: {
        type: Object,
        default: null,
    },
})

const uid = useId()
const firstNameId = `card-first-name-${uid}`
const lastNameId = `card-last-name-${uid}`
const emailId = `card-email-${uid}`
const paymentElementId = `payment-element-${uid}`

const firstName = ref(props.stripeData?.first_name ?? '')
const lastName = ref(props.stripeData?.last_name ?? '')
const email = ref(props.stripeData?.user?.email ?? '')
const errorMessage = ref('')

let stripe = null
let elements = null
let paymentElement = null
let configured = false

function applePayOptions() {
    return props.applePayRecurring ? { recurringPaymentRequest: props.applePayRecurring } : {}
}

function includeStripe(callback) {
    if (typeof window !== 'undefined' && window.Stripe) {
        callback()

        return
    }

    const documentTag = document
    const object = documentTag.createElement('script')
    const scriptTag = documentTag.getElementsByTagName('script')[0]
    object.src = 'https://js.stripe.com/v3/'
    object.addEventListener('load', () => callback(), false)
    scriptTag.parentNode.insertBefore(object, scriptTag)
}

function configureStripe() {
    if (configured || typeof window === 'undefined' || !window.Stripe) {
        return
    }

    stripe = window.Stripe(props.stripeData.publishable_key)
    elements = stripe.elements({
        clientSecret: props.stripeData.setup_intent?.client_secret,
    })

    // markRaw: keep Vue's reactivity proxy off the Stripe element.
    paymentElement = markRaw(elements.create('payment', {
        wallets: { link: 'never' },
        applePay: applePayOptions(),
        defaultValues: {
            billingDetails: {
                name: `${firstName.value} ${lastName.value}`.trim(),
                email: email.value,
            },
        },
    }))
    paymentElement.mount(`#${paymentElementId}`)
    // Lets the parent keep Subscribe disabled until a card is fully entered or a wallet is picked.
    paymentElement.on('change', (event) => {
        emit('complete', !!event.complete)
    })

    configured = true
}

function ensureConfigured() {
    includeStripe(configureStripe)
}

onMounted(() => {
    if (props.visible) {
        ensureConfigured()
    }
})

watch(() => props.visible, (isVisible) => {
    if (isVisible) {
        ensureConfigured()
    }
})

// The Pro checkout re-prices as the landlord types a property count, so keep Apple's sheet in step.
watch(() => props.applePayRecurring, () => {
    paymentElement?.update({ applePay: applePayOptions() })
}, { deep: true })

/**
 * Confirm the SetupIntent and persist the payment method. Returns a plain result the caller
 * can branch on; never throws. `replaceExisting` detaches the card already on file first (the
 * "Change card" path). `settle_open_invoices` is always false here: this component never
 * silently settles an invoice — the subscribe request that follows does the charging.
 *
 * Nothing may be awaited before confirmSetup: Apple Pay only opens its sheet when Stripe is
 * called straight from the click.
 */
async function confirmAndSave({ replaceExisting = false } = {}) {
    errorMessage.value = ''

    if (!firstName.value || !lastName.value || !email.value) {
        errorMessage.value = 'Please enter your name and email.'

        return { success: false, payment_method: null, message: errorMessage.value }
    }

    if (!stripe || !paymentElement) {
        errorMessage.value = 'Card form is not ready. Please try again.'

        return { success: false, payment_method: null, message: errorMessage.value }
    }

    const { setupIntent, error } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: {
            return_url: route('customer-payment-method-created'),
            payment_method_data: {
                billing_details: {
                    name: `${firstName.value} ${lastName.value}`,
                    email: email.value,
                },
            },
        },
    })

    if (error) {
        errorMessage.value = error.message

        return { success: false, payment_method: null, message: error.message }
    }

    try {
        const response = await axios.post(route('customer-payment-method-created'), {
            setup_intent: setupIntent.id,
            request_type: 'axios',
            settle_open_invoices: false,
            replace_existing: replaceExisting,
        })

        if (response.data?.status) {
            return { success: true, payment_method: response.data.payment_method, message: response.data.message }
        }

        errorMessage.value = response.data?.message || 'Failed to attach payment method. Please try again.'

        return { success: false, payment_method: null, message: errorMessage.value }
    } catch (requestError) {
        errorMessage.value = requestError.response?.data?.message || 'Failed to attach payment method. Please try again.'

        return { success: false, payment_method: null, message: errorMessage.value }
    }
}

defineExpose({ confirmAndSave })
</script>
